import { Suspense } from 'react'
import { useUserPreferences } from '@/src/hooks/use-user-preference-port/useUserPreferencePort'
import { userPreferencesPort } from '@/infra/config/beans'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { usePeriodValueFilter } from '@/src/hooks/use-period-value-filter/usePeriodValueFilter'
import { VpIcon } from '@vtmn-play/react'
import { useTranslation } from 'react-i18next'
import { VpDivider } from '@vtmn-play/react/VpDivider'
import CardScore from '@/src/components/global/card-score/CardScore'
import BackButton from '@/src/components/global/back-button/BackButton'
import { MonthFilterSelect } from '@/src/components/month-filter-select/MonthFilterSelect'
import TeamFamilyCard from '@/src/components/my-team/team-family-card/TeamFamilyCard'
import { usePageHeaders } from '@/src/hooks/use-page-headers/usePageHeaders'
import VendorDetailSkeleton from '@/src/components/layout/vendor-detail-skeleton/VendorDetailSkeleton'
import { getVendors } from '@/infra/api/generated/vendors/vendors'
import { VendorSummaryDto } from '@/infra/api/generated/model'
import { trackEvent } from '@/src/analytics/amplitude'

const { getVendorDetails } = getVendors()

const vendorDetailQuery = (
  periodValue: string,
  userId?: string,
  teamId?: number,
  storeId?: number
) => ({
  queryKey: ['diagnoses', 'vendors', 'detail', userId, periodValue, teamId, storeId],
  queryFn: () =>
    getVendorDetails(storeId!, teamId!, userId!, {
      periodValue: periodValue,
    }),
  enabled: Boolean(userId) && Boolean(storeId) && Boolean(teamId),
})

const Details = () => {
  return (
    <div className="h-full flex flex-col">
      <Suspense fallback={<VendorDetailSkeleton />}>
        <DetailsContent />
      </Suspense>
    </div>
  )
}
const DetailsContent = () => {
  const { team, store } = useUserPreferences(userPreferencesPort)
  const { t } = useTranslation('common')
  usePageHeaders(t('pages.my-team.title'), `${team?.name}`)
  const { userId } = useParams<{ userId: string }>()
  const { periodValue, setPeriodValue } = usePeriodValueFilter()
  const { data: vendorDetail } = useSuspenseQuery(
    vendorDetailQuery(periodValue, userId, team?.id, store?.id)
  )
  const navigate = useNavigate()

  const handleFamilyCardClick = (diagnosisId: string) => {
    trackEvent('CONTINUE_ACTION_PLAN_BUTTON_CLICK', {
      diagnosisId,
      source: 'my-team/vendor',
    })
    void navigate(`/diagnoses/${diagnosisId}/actions`, { state: { source: 'myteam' } })
  }

  return (
    <div className="h-full overflow-auto flex flex-col">
      <div className="p-5 flex flex-col items-start gap-5">
        <BackButton onBrand />
        <VendorDetailSummary {...vendorDetail.summary} />
      </div>
      <div className="flex-1 bg-white rounded-t-2xl p-5 flex flex-col gap-3">
        <MonthFilterSelect value={periodValue} onChange={setPeriodValue} />
        {vendorDetail.diagnoses.map((hist) => (
          <TeamFamilyCard
            key={hist.diagnosisId}
            diagnosisId={hist.diagnosisId}
            userId={hist.userId}
            family={hist.family}
            score={hist.score}
            daysLeft={hist.daysLeft}
            onClick={handleFamilyCardClick}
          />
        ))}
      </div>
    </div>
  )
}

const VendorDetailSummary = ({ userId, averageScore, countDiagnoses }: VendorSummaryDto) => {
  const { t } = useTranslation('common')
  return (
    <div data-testid="vendor-detail-summary" className="w-full flex gap-3">
      <div className="flex-1 flex items-center gap-3">
        <div className="h-10 w-10 p-2 rounded-full bg-on-brand-quiet">
          <VpIcon name="user" />
        </div>
        <div className="flex-1 flex flex-col gap-1">
          <span className="font-semibold text-white">{userId}</span>
          <span data-testid="vendor-detail-summary-count-diagnoses" className="text-sm text-white">
            {t('pages.my-team.vendors.count-diagnoses', { count: countDiagnoses })}
          </span>
        </div>
      </div>
      <VpDivider orientation="vertical" />
      <div className="flex flex-col items-center">
        <CardScore size="xl" score={averageScore} isOnBrand scoreColored />
        <span className="text-white">{t('global.compliance')}</span>
      </div>
    </div>
  )
}

export default Details
